import {
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { resolvePublicFeatureFlags } from '@/shared/data/featureFlags';
import { appearanceFeatureFlags } from '@/shared/featureFlags/appearanceFeatureFlags';
import { ThemeModeContext } from './ThemeModeContext';
import type { PaletteMode } from '@/ui/primitives';

export const themeModeStorageKey = 'flowqa-theme-mode';
const themeModeStorageVersionKey = 'flowqa-theme-mode-version';
const themeModeStorageCustomisedKey = 'flowqa-theme-mode-customised';
const themeModeStorageVersion = '2026-05-appearance-flags';

const shouldUseLocalLightDefault =
  import.meta.env.DEV && import.meta.env.VITE_DEFAULT_THEME_MODE === 'light';
const defaultThemeMode: PaletteMode = shouldUseLocalLightDefault ? 'light' : 'dark';

const isPaletteMode = (value: string | null): value is PaletteMode =>
  value === 'light' || value === 'dark';

const readStoredThemeMode = (): PaletteMode => {
  if (typeof window === 'undefined') return defaultThemeMode;
  const storedVersion = window.localStorage.getItem(themeModeStorageVersionKey);
  if (storedVersion !== themeModeStorageVersion) {
    return defaultThemeMode;
  }
  const storedMode = window.localStorage.getItem(themeModeStorageKey);
  return isPaletteMode(storedMode) ? storedMode : defaultThemeMode;
};

const hasCustomisedStoredThemeMode = (): boolean =>
  typeof window !== 'undefined' &&
  window.localStorage.getItem(themeModeStorageCustomisedKey) === 'true';

const persistResolvedThemeDefault = (nextMode: PaletteMode): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(themeModeStorageVersionKey, themeModeStorageVersion);
  window.localStorage.setItem(themeModeStorageKey, nextMode);
};

export const ThemeModeProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [mode, setModeState] = useState<PaletteMode>(() => readStoredThemeMode());

  const setMode = useCallback((nextMode: PaletteMode): void => {
    setModeState(nextMode);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(themeModeStorageVersionKey, themeModeStorageVersion);
      window.localStorage.setItem(themeModeStorageCustomisedKey, 'true');
      window.localStorage.setItem(themeModeStorageKey, nextMode);
    }
  }, []);

  const toggleMode = useCallback((): void => {
    setModeState((currentMode) => {
      const nextMode = currentMode === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(themeModeStorageVersionKey, themeModeStorageVersion);
        window.localStorage.setItem(themeModeStorageCustomisedKey, 'true');
        window.localStorage.setItem(themeModeStorageKey, nextMode);
      }
      return nextMode;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || hasCustomisedStoredThemeMode()) {
      return;
    }
    let cancelled = false;

    const resolveDefaultMode = async (): Promise<void> => {
      try {
        const result = await resolvePublicFeatureFlags([appearanceFeatureFlags.lightModeDefault]);
        if (cancelled || hasCustomisedStoredThemeMode()) {
          return;
        }
        const nextMode = result.flags[appearanceFeatureFlags.lightModeDefault]
          ? 'light'
          : defaultThemeMode;
        persistResolvedThemeDefault(nextMode);
        setModeState(nextMode);
      } catch {
        if (cancelled || hasCustomisedStoredThemeMode()) {
          return;
        }
        persistResolvedThemeDefault(defaultThemeMode);
        setModeState(defaultThemeMode);
      }
    };

    void resolveDefaultMode();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};
